/**
 * 激活码页的弹窗：生成、生成结果、码详情（含兑换记录与奖池分布）、封禁名单、备注修改、确认。
 *
 * 背景：Alice 的激活码页把「生成、查看、改、删、封禁名单」都放在弹窗里。本仓的后端接口在
 *   gateway_control/routes_admin_users.py（/control/admin/activation-*），金额都是整花瓣的 unit，页面里统一用花瓣填写。
 * 设计意图：
 *   - 生成弹窗三种类型：固定面额、抽奖（区间随机）、奖池（分档预分配，见 PoolEditor）；批量机器生成与自定义码两种模式。
 *     奖池容量 = 码数量 × 总次数上限（自定义码时码数量为 1），份数合计不对时「生成」不可点。
 *   - 生成结果弹窗只展示一次完整码值并给复制按钮；之后列表里仍可查，但批次导出才有完整清单。
 *   - 详情弹窗里的修改只提交改过的字段；奖池码不允许改总次数（奖池容量在创建时已定）。
 * 关键约束：有输入框的弹窗不允许点遮罩关闭；写操作要 admin（页面按 isAdmin 决定是否渲染这些入口，后端为准）；
 *   有效期必填，且晚于生效时间（后端校验，前端只做非空与顺序的快速提示）。
 */
import { useEffect, useMemo, useState } from 'react'
import { usersApi, type CodeBlock, type CodeDetail, type CodeRow, type GenerateResult } from '@ap/api-client/admin-users'
import { UNITS_PER_PETAL } from '@ap/api-client/money'
import { fmtDateTime, useToast } from '@ap/ui'
import { Pager } from '../kit.tsx'
import { PILL, Pill } from '../kit-runtime2.tsx'
import { FormRow, IntInput, UModal, copyText, fieldStyle, inDays, isoToLocal, localToIso, useQuery, useU } from '../kit-users.tsx'
import { PoolEditor, tierStats, tiersToApi, type TierDraft } from './PoolEditor.tsx'

type CodeType = 'fixed' | 'lottery' | 'pool'

// ------------------------------------------------------------------ 生成

export function GenerateModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: (r: GenerateResult | { code: string }, expiresIso: string) => void }) {
  const { t, G, err, tag } = useU()
  const P = G.codes.gen
  const [mode, setMode] = useState<'batch' | 'custom'>('batch')
  const [type, setType] = useState<CodeType>('fixed')
  const [count, setCount] = useState('10')
  const [custom, setCustom] = useState('')
  const [amount, setAmount] = useState('100')
  const [amountMax, setAmountMax] = useState('')
  const [maxTotal, setMaxTotal] = useState('1')
  const [perUser, setPerUser] = useState('1')
  const [expires, setExpires] = useState(() => inDays(30))
  const [validFrom, setValidFrom] = useState('')
  const [note, setNote] = useState('')
  const [tiers, setTiers] = useState<TierDraft[]>([{ amount: '100', count: '10' }])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setError('')
      setExpires(inDays(30))
    }
  }, [open])

  const n = mode === 'batch' ? Number(count) || 0 : 1
  const total = Number(maxTotal) || 0
  const size = n * total
  const a = Number(amount) || 0
  const aMax = Number(amountMax) || 0
  const poolOk = type !== 'pool' || (tiersToApi(tiers) !== null && tierStats(tiers).shares === size)
  const tierBudget = tierStats(tiers).total

  const problem = useMemo(() => {
    if (mode === 'batch' && !(n >= 1 && n <= 10000)) return t(G.codes.gen.count)
    if (mode === 'custom' && !/^[A-Z0-9]{4,32}$/.test(custom.trim().toUpperCase())) return t(P.customCode)
    if (total < 1) return t(P.maxTotal)
    if (!(Number(perUser) >= 1 && Number(perUser) <= total)) return t(P.maxPerUser)
    if (type !== 'pool' && a < 1) return t(P.amountRequired)
    if (type === 'lottery' && aMax <= a) return t(P.lotteryRange)
    if (!expires) return t(P.expiresRequired)
    if (validFrom && localToIso(validFrom)! >= localToIso(expires)!) return t(P.validFromBefore)
    if (!poolOk) return t(P.poolMismatch, { have: tierStats(tiers).shares, need: size })
    return ''
  }, [mode, n, custom, total, perUser, type, a, aMax, expires, validFrom, poolOk, tiers, size, t, G, P])

  const preview =
    type === 'fixed'
      ? t(P.previewFixed, { count: n.toLocaleString(tag), amount: a.toLocaleString(tag), total: (a * total * n).toLocaleString(tag) })
      : type === 'lottery'
        ? t(P.previewLottery, { count: n.toLocaleString(tag), low: a.toLocaleString(tag), high: aMax.toLocaleString(tag), total: (aMax * total * n).toLocaleString(tag) })
        : t(P.previewPool, { size: size.toLocaleString(tag), total: tierBudget.toLocaleString(tag) })

  const submit = async () => {
    const iso = localToIso(expires)
    if (!iso) {
      setError(t(P.expiresRequired))
      return
    }
    const base = {
      max_total_uses: total,
      max_uses_per_user: Number(perUser),
      valid_from: localToIso(validFrom) ?? null,
      expires_at: iso,
      note: note.trim(),
    }
    const money =
      type === 'pool'
        ? { prize_pool: { tiers: tiersToApi(tiers) ?? [] } }
        : { amount_units: a * UNITS_PER_PETAL, amount_max_units: type === 'lottery' ? aMax * UNITS_PER_PETAL : null }
    setBusy(true)
    setError('')
    try {
      if (mode === 'batch') {
        onDone(await usersApi.generateCodes({ count: n, ...base, ...money }), iso)
      } else {
        const r = await usersApi.createCode({ code: custom.trim().toUpperCase(), ...base, ...money })
        onDone({ code: r.code }, iso)
      }
    } catch (e) {
      console.error('[g2] 生成激活码失败', e)
      setError(err(e))
    } finally {
      setBusy(false)
    }
  }

  const seg = <T extends string>(value: T, cur: T, set: (v: T) => void, label: string) => (
    <button className={`btn btn-secondary btn-sm${cur === value ? ' active' : ''}`} onClick={() => set(value)} type="button">
      {label}
    </button>
  )

  return (
    <UModal
      open={open}
      title={t(P.title)}
      onClose={onClose}
      busy={busy}
      width={680}
      footer={
        <>
          <button className="btn btn-secondary" disabled={busy} onClick={onClose}>
            {t(G.common.cancel)}
          </button>
          <button className="btn btn-primary" disabled={busy || !!problem} title={problem || undefined} onClick={submit}>
            {t(P.submit)}
          </button>
        </>
      }
    >
      {error && <div className="ops-note" style={{ color: 'var(--error-text)', marginTop: 0 }}>{error}</div>}
      <div className="u2-seg" style={{ marginBottom: 12 }}>
        {seg('batch', mode, setMode, t(P.modeBatch))}
        {seg('custom', mode, setMode, t(P.modeCustom))}
      </div>
      {mode === 'batch' ? (
        <FormRow label={t(P.count)}>
          <IntInput value={count} onChange={setCount} style={{ ...fieldStyle, width: '100%' }} />
        </FormRow>
      ) : (
        <FormRow label={t(P.customCode)}>
          <input value={custom} maxLength={32} placeholder={t(P.customPh)} onChange={(e) => setCustom(e.target.value.toUpperCase())} style={{ ...fieldStyle, width: '100%', fontFamily: 'var(--mono)' }} />
        </FormRow>
      )}
      <div className="u2-seg" style={{ marginBottom: 12 }}>
        {seg('fixed', type, setType, t(P.typeFixed))}
        {seg('lottery', type, setType, t(P.typeLottery))}
        <span title={t(P.typePoolTip)}>{seg('pool', type, setType, t(P.typePool))}</span>
      </div>
      {type === 'fixed' && (
        <FormRow label={t(P.amount)}>
          <IntInput value={amount} onChange={setAmount} style={{ ...fieldStyle, width: '100%' }} />
        </FormRow>
      )}
      {type === 'lottery' && (
        <div className="u2-grid2">
          <FormRow label={t(P.amountLow)}>
            <IntInput value={amount} onChange={setAmount} style={{ ...fieldStyle, width: '100%' }} />
          </FormRow>
          <FormRow label={t(P.amountMax)}>
            <IntInput value={amountMax} onChange={setAmountMax} style={{ ...fieldStyle, width: '100%' }} />
          </FormRow>
        </div>
      )}
      <div className="u2-grid2">
        <FormRow label={t(P.maxTotal)}>
          <IntInput value={maxTotal} onChange={setMaxTotal} style={{ ...fieldStyle, width: '100%' }} />
        </FormRow>
        <FormRow label={t(P.maxPerUser)}>
          <IntInput value={perUser} onChange={setPerUser} style={{ ...fieldStyle, width: '100%' }} />
        </FormRow>
        <FormRow label={t(P.expires)}>
          <input type="datetime-local" value={expires} onChange={(e) => setExpires(e.target.value)} style={{ ...fieldStyle, width: '100%' }} />
        </FormRow>
        <FormRow label={t(P.validFrom)}>
          <input type="datetime-local" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} style={{ ...fieldStyle, width: '100%' }} />
        </FormRow>
      </div>
      {type === 'pool' && (
        <div style={{ marginBottom: 12 }}>
          <PoolEditor tiers={tiers} onChange={setTiers} size={size} />
        </div>
      )}
      <FormRow label={t(P.note)}>
        <input value={note} maxLength={500} placeholder={t(P.notePh)} onChange={(e) => setNote(e.target.value)} style={{ ...fieldStyle, width: '100%' }} />
      </FormRow>
      <div className="ops-note" style={{ marginTop: 4 }}>
        {preview}
      </div>
    </UModal>
  )
}

// ------------------------------------------------------------------ 生成结果

export function ResultModal({ result, expiresAt, onClose }: { result: GenerateResult | { code: string } | null; expiresAt: string; onClose: () => void }) {
  const { t, G, locale } = useU()
  const P = G.codes.gen
  const toast = useToast()
  if (!result) return null
  const codes = 'codes' in result ? result.codes : [result.code]
  const date = fmtDateTime(expiresAt, locale)
  const copy = async (text: string) => {
    if (await copyText(text)) toast.ok(t(G.common.copied))
    else toast.error(t(G.common.copyFail))
  }
  return (
    <UModal
      open
      title={'codes' in result ? t(P.resultTitle) : t(P.customDone, { code: result.code })}
      onClose={onClose}
      width={560}
      footer={
        <>
          <button className="btn btn-secondary" onClick={() => copy(codes.join('\n'))}>
            {t(P.copyAll)}
          </button>
          <button className="btn btn-secondary" onClick={() => copy(codes.map((c) => t(P.shareText, { code: c, date })).join('\n\n'))}>
            {t(P.copyShare)}
          </button>
          <button className="btn btn-primary" onClick={onClose}>
            {t(P.done)}
          </button>
        </>
      }
    >
      {'codes' in result && <div className="ops-note" style={{ marginTop: 0 }}>{t(P.resultSummary, { batch: result.batch_id, n: codes.length })}</div>}
      <div className="u2-codebox">{codes.join('\n')}</div>
    </UModal>
  )
}

// ------------------------------------------------------------------ 详情

export function CodeDetailModal({ row, isAdmin, onClose, onChanged }: { row: CodeRow | null; isAdmin: boolean; onClose: () => void; onChanged: () => void }) {
  const { t, G, err, pet, num, locale } = useU()
  const D = G.codes.detail
  const toast = useToast()
  const id = row?.id ?? 0
  const detail = useQuery(() => usersApi.code(id), [id], row !== null)
  const [off, setOff] = useState(0)
  const reds = useQuery(() => usersApi.redemptions(id, 10, off), [id, off], row !== null)
  const [edit, setEdit] = useState(false)
  const [expires, setExpires] = useState('')
  const [note, setNote] = useState('')
  const [total, setTotal] = useState('')
  const [perUser, setPerUser] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setOff(0)
    setEdit(false)
    setError('')
  }, [id])

  const d: CodeDetail | null = detail.data
  const startEdit = () => {
    if (!d) return
    setExpires(isoToLocal(d.expires_at))
    setNote(d.note)
    setTotal(String(d.max_total_uses))
    setPerUser(String(d.max_uses_per_user))
    setError('')
    setEdit(true)
  }
  const save = async () => {
    if (!d) return
    const body: Record<string, unknown> = {}
    const iso = localToIso(expires)
    if (iso && new Date(iso).getTime() !== new Date(d.expires_at).getTime()) body.expires_at = iso
    if (note.trim() !== d.note) body.note = note.trim()
    if (!d.pool_id && Number(total) !== d.max_total_uses) body.max_total_uses = Number(total)
    if (Number(perUser) !== d.max_uses_per_user) body.max_uses_per_user = Number(perUser)
    if (Object.keys(body).length === 0) {
      setEdit(false)
      return
    }
    setBusy(true)
    setError('')
    try {
      await usersApi.patchCode(d.id, body)
      toast.ok(t(D.saved))
      setEdit(false)
      detail.reload()
      onChanged()
    } catch (e) {
      console.error('[g2] 修改激活码失败', e)
      setError(err(e))
    } finally {
      setBusy(false)
    }
  }

  const kv = (label: string, value: React.ReactNode) => (
    <div className="d-row">
      <span className="d-label">{label}</span>
      <span className="d-value">{value}</span>
    </div>
  )
  const amountText = d ? (d.amount_max_units ? `${num(d.amount_units)} ~ ${num(d.amount_max_units)}` : pet(d.amount_units)) : ''

  return (
    <UModal
      open={row !== null}
      title={t(D.title)}
      onClose={onClose}
      busy={busy}
      width={680}
      footer={
        edit ? (
          <>
            <button className="btn btn-secondary" disabled={busy} onClick={() => setEdit(false)}>
              {t(G.common.cancel)}
            </button>
            <button className="btn btn-primary" disabled={busy} onClick={save}>
              {t(G.common.save)}
            </button>
          </>
        ) : (
          <>
            {isAdmin && d && (
              <button className="btn btn-secondary" onClick={startEdit}>
                {t(D.edit)}
              </button>
            )}
            <button className="btn btn-primary" onClick={onClose}>
              {t(G.common.close)}
            </button>
          </>
        )
      }
    >
      {detail.error != null && <div className="ops-note" style={{ color: 'var(--error-text)', marginTop: 0 }}>{t(G.common.loadFailed, { msg: err(detail.error) })}</div>}
      {!d && detail.loading && <div className="u2-note">{t(G.common.loading)}</div>}
      {d && !edit && (
        <>
          <div className="d-section" style={{ padding: '4px 15px' }}>
            {kv(t(D.code), <span className="mono">{d.code}</span>)}
            {kv(t(D.batch), <span className="mono">{d.batch_id || '—'}</span>)}
            {kv(t(D.amount), amountText)}
            {kv(t(D.uses), `${d.used_count} / ${d.max_total_uses}`)}
            {kv(t(D.perUser), d.max_uses_per_user)}
            {kv(t(D.validFrom), d.valid_from ? fmtDateTime(d.valid_from, locale) : '—')}
            {kv(t(D.expires), fmtDateTime(d.expires_at, locale))}
            {kv(t(D.status), <Pill tone={d.status === 'active' ? PILL.green : PILL.gray}>{t(d.status === 'active' ? G.codes.statusActive : G.codes.statusDisabled)}</Pill>)}
            {kv(t(D.redeemed), pet(d.redeemed_units))}
            {kv(t(D.createdBy), <span className="mono">{d.created_by || '—'}</span>)}
            {kv(t(D.createdAt), fmtDateTime(d.created_at, locale))}
            {kv(t(D.note), d.note || '—')}
          </div>
          {d.pool && (
            <div className="d-section">
              <div className="d-section-title">{t(D.poolTitle)}</div>
              <div className="u2-note" style={{ marginTop: 0, marginBottom: 8 }}>
                {t(D.poolSummary, { claimed: d.pool.claimed_count, size: d.pool.size, paid: num(d.pool.claimed_units), total: num(d.pool.total_units) })}
              </div>
              <div className="user-detail-table-wrap" style={{ fontSize: 12 }}>
                <table style={{ minWidth: 0 }}>
                  <thead>
                    <tr>
                      <th>{t(D.poolColAmount)}</th>
                      <th>{t(D.poolColCount)}</th>
                      <th>{t(D.poolColClaimed)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.pool.tiers.map((x) => (
                      <tr key={x.amount_units}>
                        <td>{num(x.amount_units)}</td>
                        <td>{x.count}</td>
                        <td>{x.claimed ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
      {d && edit && (
        <>
          {error && <div className="ops-note" style={{ color: 'var(--error-text)', marginTop: 0 }}>{error}</div>}
          <FormRow label={t(D.editExpiry)}>
            <input type="datetime-local" value={expires} onChange={(e) => setExpires(e.target.value)} style={{ ...fieldStyle, width: '100%' }} />
          </FormRow>
          <div className="u2-grid2">
            <FormRow label={t(G.codes.gen.maxTotal)}>
              <IntInput value={total} disabled={d.pool_id !== null} onChange={setTotal} style={{ ...fieldStyle, width: '100%' }} />
            </FormRow>
            <FormRow label={t(G.codes.gen.maxPerUser)}>
              <IntInput value={perUser} onChange={setPerUser} style={{ ...fieldStyle, width: '100%' }} />
            </FormRow>
          </div>
          <FormRow label={t(D.editNote)}>
            <input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} style={{ ...fieldStyle, width: '100%' }} />
          </FormRow>
        </>
      )}
      {d && !edit && (
        <div className="d-section">
          <div className="d-section-title">{t(D.redemptions)}</div>
          <div className="user-detail-table-wrap" style={{ fontSize: 12 }}>
            <table style={{ minWidth: 0 }}>
              <thead>
                <tr>
                  <th>{t(D.colTime)}</th>
                  <th>{t(D.colUser)}</th>
                  <th>{t(D.colAmount)}</th>
                  <th>{t(D.colBalance)}</th>
                </tr>
              </thead>
              <tbody>
                {reds.data && reds.data.redemptions.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>
                      {t(D.noRedemptions)}
                    </td>
                  </tr>
                )}
                {reds.data?.redemptions.map((r, i) => (
                  <tr key={`${r.account_id}-${r.created_at}-${i}`}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.created_at, locale)}</td>
                    <td title={r.account_id}>{r.display_name || r.email || r.account_id.slice(0, 8)}</td>
                    <td>{num(r.amount_units)}</td>
                    <td>{r.balance_after_units === null ? '—' : num(r.balance_after_units)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '10px 0 0', display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
            <Pager offset={off} limit={10} total={reds.data?.total ?? 0} onChange={setOff} />
          </div>
        </div>
      )}
    </UModal>
  )
}

// ------------------------------------------------------------------ 封禁名单

export function BlocksModal({ open, isAdmin, onClose }: { open: boolean; isAdmin: boolean; onClose: () => void }) {
  const { t, G, err, locale } = useU()
  const B = G.codes.blocks
  const toast = useToast()
  const list = useQuery(() => usersApi.blocks(), [], open)
  const [busy, setBusy] = useState<string | null>(null)

  const unblock = async (b: CodeBlock, reset: boolean) => {
    setBusy(b.account_id)
    try {
      await usersApi.unblock(b.account_id, reset)
      toast.ok(t(B.unblocked))
      list.reload()
    } catch (e) {
      toast.error(err(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <UModal open={open} title={t(B.title)} onClose={onClose} width={720} dismissOnMask footer={<button className="btn btn-primary" onClick={onClose}>{t(G.common.close)}</button>}>
      <div className="ops-note" style={{ marginTop: 0 }}>{t(B.desc)}</div>
      {list.error != null && <div className="ops-note" style={{ color: 'var(--error-text)' }}>{t(G.common.loadFailed, { msg: err(list.error) })}</div>}
      <div className="user-detail-table-wrap" style={{ fontSize: 12 }}>
        <table style={{ minWidth: 0 }}>
          <thead>
            <tr>
              <th>{t(B.colUser)}</th>
              <th>{t(B.colLevel)}</th>
              <th>{t(B.colUntil)}</th>
              {isAdmin && <th>{t(B.colActions)}</th>}
            </tr>
          </thead>
          <tbody>
            {list.data && list.data.blocks.length === 0 && (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>
                  {t(B.empty)}
                </td>
              </tr>
            )}
            {list.data?.blocks.map((b) => (
              <tr key={b.account_id}>
                <td title={b.account_id}>
                  <div>{b.display_name || b.email}</div>
                  <div className="mono" style={{ color: 'var(--text-tertiary)' }}>{b.email}</div>
                </td>
                <td>
                  L{b.block_level} · {b.fail_count}
                </td>
                <td>{b.blocked_until ? fmtDateTime(b.blocked_until, locale) : '—'}</td>
                {isAdmin && (
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" disabled={busy === b.account_id} onClick={() => unblock(b, false)}>
                        {t(B.unblock)}
                      </button>
                      <button className="btn btn-secondary btn-sm" disabled={busy === b.account_id} onClick={() => unblock(b, true)}>
                        {t(B.unblockReset)}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </UModal>
  )
}

// ------------------------------------------------------------------ 备注与确认

export function NoteModal({ batch, initial, onClose, onDone }: { batch: string | null; initial: string; onClose: () => void; onDone: (n: number) => void }) {
  const { t, G, err } = useU()
  const [note, setNote] = useState(initial)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    if (batch) {
      setNote(initial)
      setError('')
    }
  }, [batch, initial])
  if (!batch) return null
  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      const r = await usersApi.noteBatch(batch, note.trim())
      onDone(r.updated_count)
    } catch (e) {
      console.error('[g2] 修改批次备注失败', e)
      setError(err(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <UModal
      open
      title={t(G.codes.batchNoteTitle)}
      onClose={onClose}
      busy={busy}
      width={460}
      footer={
        <>
          <button className="btn btn-secondary" disabled={busy} onClick={onClose}>
            {t(G.common.cancel)}
          </button>
          <button className="btn btn-primary" disabled={busy} onClick={submit}>
            {t(G.common.save)}
          </button>
        </>
      }
    >
      {error && <div className="ops-note" style={{ color: 'var(--error-text)', marginTop: 0 }}>{error}</div>}
      <FormRow label={`${t(G.common.note)} · ${batch}`}>
        <input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} style={{ ...fieldStyle, width: '100%' }} />
      </FormRow>
    </UModal>
  )
}

/** 没有输入框的确认弹窗（删除码、删除批次）：允许点遮罩关闭，提交中锁住。 */
export function ConfirmDialog({ text, onClose, onConfirm }: { text: string | null; onClose: () => void; onConfirm: () => Promise<void> }) {
  const { t, G, err } = useU()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    if (text) setError('')
  }, [text])
  if (text === null) return null
  const run = async () => {
    setBusy(true)
    setError('')
    try {
      await onConfirm()
    } catch (e) {
      console.error('[g2] 确认操作失败', e)
      setError(err(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <UModal
      open
      title={t(G.codes.deleteBtn)}
      onClose={onClose}
      busy={busy}
      width={440}
      dismissOnMask
      footer={
        <>
          <button className="btn btn-secondary" disabled={busy} onClick={onClose}>
            {t(G.common.cancel)}
          </button>
          <button className="btn btn-danger" disabled={busy} onClick={run}>
            {t(G.codes.deleteBtn)}
          </button>
        </>
      }
    >
      <div style={{ fontSize: 13, lineHeight: 1.7 }}>{text}</div>
      {error && <div className="ops-note" style={{ color: 'var(--error-text)' }}>{error}</div>}
    </UModal>
  )
}

